import type { InsightChartConfig } from "./types";

/**
 * Summary-insights cards on the trials list, CWTRC-2063.
 *
 * Adding a card is an entry here — a path plus the `type` that says what shape
 * the response has. Every endpoint takes the `GET /trials` filter set, so each
 * card counts the same set the table lists.
 *
 * Card order matches the design: status, yield analysis, planned dates.
 */

/**
 * Execution-status colours. Same values the protocols dashboard uses for the
 * same statuses, so a status reads identically across both pages.
 */
export const TRIAL_STATUS_COLORS: Record<string, string> = {
  activated: "#14803C",
  ready: "#0071CD",
  draft: "#C1462B",
};

/** Declaration order the API returns statuses in. */
export const TRIAL_STATUS_ORDER = ["activated", "ready", "draft"];

export const TRIAL_INSIGHTS_CHARTS: InsightChartConfig[] = [
  {
    // GET /trials/insights/trials -> { retrieved_at, data: [{label, value}] }
    id: "execution-status",
    type: "breakdown",
    path: "/trials/insights/trials",
    titleKey: "summaryInsights.trialStatus",
    labelKeyPrefix: "summaryInsights.status",
    displayOrder: TRIAL_STATUS_ORDER,
    colors: TRIAL_STATUS_COLORS,
  },
  {
    // GET /trials/insights/yield-readiness
    //   -> { retrieved_at, total_trials, yield_ready_trials }
    //
    // Readiness comes from analytics-valet, which is scoped by property, so
    // this endpoint promotes property_id from optional to required. It also
    // surfaces that upstream's failures as 5xx rather than reporting zero —
    // deliberately, so "could not check" and "none are ready" stay distinct.
    id: "yield-readiness",
    type: "counts",
    path: "/trials/insights/yield-readiness",
    titleKey: "summaryInsights.yieldAnalysis",
    valueField: "yield_ready_trials",
    totalField: "total_trials",
    variant: "ring",
    captionKey: "summaryInsights.withYieldResults",
    color: "#14803C",
    requiresPropertyId: true,
  },
  {
    // GET /trials/insights/planned
    //   -> { retrieved_at, total_trials, planned_trials }
    id: "planned",
    type: "counts",
    path: "/trials/insights/planned",
    titleKey: "summaryInsights.plannedDates",
    valueField: "planned_trials",
    totalField: "total_trials",
    variant: "figure",
    captionKey: "summaryInsights.withinPlannedDates",
    color: "#0071CD",
    // "Planned" is defined against the server's today, and retrieved_at is
    // that same instant — so the card is titled with it, as in the design.
    titleFromRetrievedAt: true,
  },
];
